export interface Patient {
  patient_id: string;
  mrn: string;
  last_name: string;
  first_name: string;
  document_count: number;
}

export interface DocumentKindGroupSummary {
  kind: string;
  label: string;
  documentCount: number;
}

export interface DocumentSummary {
  document_count: number;
  patient_count: number;
  size_bytes: number;
  /** SQ-172: counts sum to document_count — one query grouped, not a second one. */
  groups?: DocumentKindGroupSummary[];
  /**
   * SQ-181: how many more documents ticking "include documents held from billing" would
   * add. Zero once it is ticked — nothing is being withheld any more.
   */
  held_withheld_count?: number;
}

export const PAYER_TYPE_LABELS: Record<string, string> = {
  COMMERCIAL_INSURANCE: 'Commercial Insurance',
  HOSPICE: 'Hospice',
  MANAGED_CARE_A: 'Managed Care A',
  MANAGED_CARE_B: 'Managed Care B',
  MEDICAID: 'Medicaid',
  MEDICARE_A: 'Medicare Part A',
  MEDICARE_B: 'Medicare Part B',
  OTHER: 'Other',
  PRIVATE_PAY: 'Private Pay',
  VA: 'VA',
};

export interface DocTypeOption {
  value: string;
  label: string;
}

export const DOC_TYPE_OPTIONS: DocTypeOption[] = [
  { value: 'EVAL', label: 'Evaluation' },
  { value: 'RECERT', label: 'Recertification' },
  { value: 'SOAP', label: 'Daily Note' },
  { value: 'DISCHARGE', label: 'Discharge' },
  { value: 'WPN', label: 'Progress Note' },
  { value: 'TRANS_EVAL', label: 'Transition Eval' },
  { value: 'SLM', label: 'SLM' },
  { value: 'MISC', label: 'Misc' },
];

// TODO SQ-85 (PP-2): Replace with response from GET /documents/filters/payers?date_from=&date_to=
// Returns available payer types for the selected date range.
export const MOCK_PAYER_TYPES = [
  'COMMERCIAL_INSURANCE',
  'HOSPICE',
  'MANAGED_CARE_A',
  'MANAGED_CARE_B',
  'MEDICAID',
  'MEDICARE_A',
  'MEDICARE_B',
  'OTHER',
  'PRIVATE_PAY',
  'VA',
];

// TODO SQ-85 (PP-2): Replace with payer_names map from GET /documents/filters/payers?date_from=&date_to=
// Keyed by payer_type; values are the payer names available under that type.
export const MOCK_PAYER_NAMES: Record<string, string[]> = {
  MANAGED_CARE_A: [
    'Managed A Levels Cigna HS MGD A',
    'Managed A Capital Health Levels',
    'Managed A Capital Health PDPM',
    'HMO A PA Health Wellness',
  ],
  MANAGED_CARE_B: ['Capital Health Plan CHP B', 'Capital Health Plan CHP'],
  MEDICARE_B: ['Patients Advocate B'],
  MEDICAID: ['Managed Medicaid Plan'],
  VA: ['VA LONG STAY DAYS 101'],
  PRIVATE_PAY: ['Private Pay - Standard'],
  COMMERCIAL_INSURANCE: ['Blue Cross Blue Shield', 'Humana', 'UnitedHealth', 'Aetna'],
};

// TODO SQ-84 (PP-5): Replace with response from GET /documents/filters/patients
// Accepts: date_from, date_to, payer_type, payer_name, discipline[], doc_type[]
// Returns: Patient[] scoped to the active filters.
export const MOCK_PATIENTS: Patient[] = [
  {
    patient_id: '1',
    mrn: 'MRN-00142',
    last_name: 'Johnson',
    first_name: 'Margaret',
    document_count: 14,
  },
  {
    patient_id: '2',
    mrn: 'MRN-00219',
    last_name: 'Williams',
    first_name: 'Robert',
    document_count: 7,
  },
  {
    patient_id: '3',
    mrn: 'MRN-00387',
    last_name: 'Davis',
    first_name: 'Patricia',
    document_count: 22,
  },
  {
    patient_id: '4',
    mrn: 'MRN-00451',
    last_name: 'Brown',
    first_name: 'Charles',
    document_count: 5,
  },
  {
    patient_id: '5',
    mrn: 'MRN-00563',
    last_name: 'Martinez',
    first_name: 'Linda',
    document_count: 18,
  },
  {
    patient_id: '6',
    mrn: 'MRN-00614',
    last_name: 'Garcia',
    first_name: 'James',
    document_count: 9,
  },
  {
    patient_id: '7',
    mrn: 'MRN-00728',
    last_name: 'Anderson',
    first_name: 'Barbara',
    document_count: 31,
  },
  {
    patient_id: '8',
    mrn: 'MRN-00835',
    last_name: 'Taylor',
    first_name: 'William',
    document_count: 3,
  },
  {
    patient_id: '9',
    mrn: 'MRN-00941',
    last_name: 'Thomas',
    first_name: 'Susan',
    document_count: 11,
  },
  {
    patient_id: '10',
    mrn: 'MRN-01047',
    last_name: 'Hernandez',
    first_name: 'David',
    document_count: 16,
  },
  {
    patient_id: '11',
    mrn: 'MRN-01153',
    last_name: 'Moore',
    first_name: 'Jessica',
    document_count: 8,
  },
  {
    patient_id: '12',
    mrn: 'MRN-01261',
    last_name: 'Jackson',
    first_name: 'Richard',
    document_count: 25,
  },
  {
    patient_id: '13',
    mrn: 'MRN-01374',
    last_name: 'Lee',
    first_name: 'Dorothy',
    document_count: 6,
  },
  {
    patient_id: '14',
    mrn: 'MRN-01482',
    last_name: 'White',
    first_name: 'Joseph',
    document_count: 19,
  },
  {
    patient_id: '15',
    mrn: 'MRN-01591',
    last_name: 'Harris',
    first_name: 'Nancy',
    document_count: 12,
  },
];

// TODO SQ-86 (PP-6): Replace with response from GET /documents/available
// Accepts: date_from, date_to, payer_type, payer_name, discipline[], doc_type[], patient_ids[], date_scope
// Returns: DocumentSummary { document_count, patient_count, size_bytes }
export const MOCK_SUMMARY: DocumentSummary = {
  document_count: 206,
  patient_count: 15,
  size_bytes: 54_500_000,
};

export type JobStatus =
  | 'QUEUED'
  | 'PROCESSING'
  | 'IN_PROGRESS'
  | 'COMPLETE'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface JobRequestDetails {
  filters_applied: {
    date_from: string;
    date_to: string;
    payer_type?: string;
    payer_name?: string;
    discipline?: string[];
    doc_type?: string[];
    patient_ids?: string[];
  };
  file_organization: {
    date_scope: string;
    group_by: string;
    document_handling: string;
    order_by: string;
    /** SQ-163: `zip` or `single_pdf`. */
    deliver_as?: string;
  };
  //  SQ-163 persists these alongside the filters. The optimistic row and the server's
  //  reply have to describe the same thing — the poll replaces one with the other, and a
  //  row that changes shape mid-flight reads as a glitch.
  /** SQ-148: the reference the user typed about the request this packet answers. */
  adr_reference?: string;
  /** `explicit` when the user picked documents by hand rather than by filter. */
  selection_mode?: string;
  selected_document_count?: number;
}

export interface DownloadJob {
  job_id: string;
  job_name: string;
  status: JobStatus;
  progress_pct: number;
  created_at: string;
  completed_at?: string;
  document_count: number;
  patient_count: number;
  size_bytes?: number;
  request_details?: JobRequestDetails;
}

// TODO SQ-89 (PP-10): Replace with response from GET /documents/download-jobs
// Returns all jobs for the authenticated user across all sessions.
// Poll every 10s while any job has status QUEUED or PROCESSING.
// Job shape must include: job_id, job_name, status, progress_pct, created_at,
// completed_at (optional), document_count, patient_count, size_bytes (optional).
export const MOCK_JOBS: DownloadJob[] = [
  {
    job_id: 'job-001',
    job_name: '2025-01-01–2025-03-31 · Medicare Part A',
    status: 'COMPLETE',
    progress_pct: 100,
    created_at: '2026-03-10T09:15:00Z',
    completed_at: '2026-03-10T09:18:42Z',
    document_count: 143,
    patient_count: 11,
    size_bytes: 37_800_000,
    request_details: {
      filters_applied: { date_from: '2025-01-01', date_to: '2025-03-31', payer_type: 'MEDICARE_A' },
      file_organization: {
        date_scope: 'range',
        group_by: 'patient',
        document_handling: 'subfolder',
        order_by: 'date_discipline',
      },
    },
  },
  {
    job_id: 'job-002',
    job_name: '2025-01-01–2025-06-30',
    status: 'COMPLETE',
    progress_pct: 100,
    created_at: '2026-03-08T14:22:00Z',
    completed_at: '2026-03-08T14:26:15Z',
    document_count: 206,
    patient_count: 15,
    size_bytes: 54_500_000,
    request_details: {
      filters_applied: { date_from: '2025-01-01', date_to: '2025-06-30', discipline: ['PT', 'OT'] },
      file_organization: {
        date_scope: 'episode',
        group_by: 'patient',
        document_handling: 'subfolder',
        order_by: 'discipline_date',
      },
    },
  },
  {
    job_id: 'job-003',
    job_name: '2025-04-01–2025-06-30 · Medicaid',
    status: 'FAILED',
    progress_pct: 34,
    created_at: '2026-03-07T11:05:00Z',
    document_count: 0,
    patient_count: 0,
    request_details: {
      filters_applied: { date_from: '2025-04-01', date_to: '2025-06-30', payer_type: 'MEDICAID' },
      file_organization: {
        date_scope: 'range',
        group_by: 'patient',
        document_handling: 'subfolder',
        order_by: 'date_discipline',
      },
    },
  },
  {
    job_id: 'job-004',
    job_name: '2026-01-01–2026-03-12',
    status: 'PROCESSING',
    progress_pct: 62,
    created_at: '2026-03-12T08:00:00Z',
    document_count: 206,
    patient_count: 15,
    size_bytes: 54_500_000,
    request_details: {
      filters_applied: { date_from: '2026-01-01', date_to: '2026-03-12' },
      file_organization: {
        date_scope: 'range',
        group_by: 'therapist',
        document_handling: 'merge',
        order_by: 'date_discipline',
      },
    },
  },
  {
    job_id: 'job-005',
    job_name: '2026-01-01–2026-03-12 · Medicare Part A',
    status: 'COMPLETE',
    progress_pct: 100,
    created_at: '2026-03-11T16:30:00Z',
    completed_at: '2026-03-11T16:34:22Z',
    document_count: 143,
    patient_count: 11,
    size_bytes: 37_800_000,
    request_details: {
      filters_applied: {
        date_from: '2026-01-01',
        date_to: '2026-03-12',
        payer_type: 'MEDICARE_A',
        doc_type: ['SOAP', 'WPN'],
      },
      file_organization: {
        date_scope: 'payer',
        group_by: 'patient',
        document_handling: 'subfolder',
        order_by: 'date_discipline',
      },
    },
  },
];
